import React, { useState, useEffect, useCallback } from 'react';
import { getPortailCovage, setPortailCovage } from '../services/api';

/**
 * Portail Covage — la porte, et qui l'a franchie
 *
 * Le guichet donne à Covage ce qu'il nous demandait par courriel : l'état
 * d'une commande, qui est passé, et le compte rendu. En lecture seule, et
 * seulement sur la référence qu'ils fournissent.
 *
 * Cette page sert à deux choses, et c'est tout : fermer la porte sans attendre
 * un déploiement, et lire qui a consulté quoi. Un portail ouvert sur
 * l'extérieur qui ne se referme pas d'un clic est un portail qu'on finit par
 * débrancher dans l'urgence.
 */

const ADRESSE = 'https://app.valeaplanning.sbs/covage/';

const horodate = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return String(iso);
  return d.toLocaleString('fr-FR', {
    timeZone: 'Europe/Paris', day: '2-digit', month: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const ACTIONS = {
  lien_envoye:     { texte: 'lien envoyé',        couleur: 'var(--blue)' },
  session_ouverte: { texte: 'entrée',             couleur: 'var(--green)' },
  recherche:       { texte: 'recherche',          couleur: 'var(--text2)' },
  document:        { texte: 'document ouvert',    couleur: 'var(--text2)' },
  demande_refusee: { texte: 'demande refusée',    couleur: 'var(--accent)' },
  reglages:        { texte: 'réglages modifiés',  couleur: 'var(--amber)' },
};

export default function PortailCovage() {
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [domaines, setDomaines] = useState('');

  const charger = useCallback(async () => {
    try {
      const r = await getPortailCovage();
      setData(r);
      setDomaines((r.reglages.domaines || []).join(', '));
    } catch (e) { setErreur(e.response?.data?.error || e.message); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const enregistrer = async (patch) => {
    setOccupe(true); setErreur('');
    try {
      await setPortailCovage(patch);
      await charger();
    } catch (e) { setErreur(e.response?.data?.error || e.message); }
    finally { setOccupe(false); }
  };

  const r = data && data.reglages;
  const journal = (data && data.journal) || [];

  const champ = {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', fontSize: 13,
    borderRadius: 6, border: '1px solid var(--border2)',
    background: 'var(--bg)', color: 'var(--text)',
  };

  return (
    <div style={{ padding: 20, maxWidth: 1100, margin: '0 auto', width: '100%',
      minWidth: 0, boxSizing: 'border-box' }}>

      <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: '0 0 4px' }}>
        Portail Covage
      </h1>
      <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
        <a href={ADRESSE} target="_blank" rel="noreferrer" style={{ color: 'var(--blue)' }}>{ADRESSE}</a>
        {' — '}lecture seule, accès par lien envoyé à l’adresse demandée.
      </div>

      {erreur && (
        <div style={{ padding: '10px 12px', marginBottom: 12, borderRadius: 6,
          fontSize: 13, background: 'var(--bg2)', color: 'var(--accent)' }}>{erreur}</div>
      )}

      {!data && <div style={{ color: 'var(--text3)', fontSize: 13 }}>Lecture…</div>}

      {r && (
        <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
          borderLeft: '3px solid ' + (r.ouvert ? 'var(--green)' : 'var(--accent)'),
          borderRadius: 8, padding: 14, marginBottom: 18 }}>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 15, fontWeight: 700,
              color: r.ouvert ? 'var(--green)' : 'var(--accent)' }}>
              {r.ouvert ? 'Portail ouvert' : 'Portail fermé'}
            </div>
            <button onClick={() => enregistrer({ ouvert: !r.ouvert })} disabled={occupe}
              style={{ padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                borderRadius: 6, border: '1px solid ' + (r.ouvert ? 'var(--accent)' : 'var(--green)'),
                background: 'transparent', color: r.ouvert ? 'var(--accent)' : 'var(--green)' }}>
              {r.ouvert ? 'fermer le portail' : 'rouvrir le portail'}
            </button>
            <div style={{ fontSize: 12, color: 'var(--text3)' }}>
              Fermé, le formulaire répond comme d’habitude mais plus aucun lien ne part.
            </div>
          </div>

          <div style={{ marginTop: 14, maxWidth: 520 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text2)', marginBottom: 4 }}>
              Domaines autorisés
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>
              Séparés par des virgules. Une adresse d’un autre domaine ne reçoit jamais de lien,
              et le formulaire ne le lui dit pas.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={domaines} onChange={e => setDomaines(e.target.value)} style={champ} />
              <button onClick={() => enregistrer({ domaines: domaines.split(',') })}
                disabled={occupe}
                style={{ padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  borderRadius: 6, border: 0, background: 'var(--blue)', color: '#fff' }}>
                enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
            Journal des consultations
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10 }}>
            Les 300 derniers événements. Chaque recherche et chaque document ouvert y figurent,
            avec l’adresse qui les a demandés.
          </div>

          {journal.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text3)', padding: 20, textAlign: 'center' }}>
              Personne n’est encore venu.
            </div>
          )}

          {journal.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 620 }}>
                <thead>
                  <tr style={{ background: 'var(--bg2)' }}>
                    {['Quand', 'Qui', 'Quoi', 'Détail', 'Adresse IP'].map(h => (
                      <th key={h} style={{ padding: '7px 10px', fontSize: 10, fontWeight: 700,
                        color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: .3,
                        textAlign: 'left', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {journal.map((e, i) => {
                    const a = ACTIONS[e.action] || { texte: e.action, couleur: 'var(--text3)' };
                    return (
                      <tr key={i} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '6px 10px', fontSize: 12, whiteSpace: 'nowrap',
                          color: 'var(--text2)' }}>{horodate(e.at)}</td>
                        <td style={{ padding: '6px 10px', fontSize: 12 }}>{e.email || '—'}</td>
                        <td style={{ padding: '6px 10px', fontSize: 11, fontWeight: 700,
                          color: a.couleur, whiteSpace: 'nowrap' }}>{a.texte}</td>
                        <td style={{ padding: '6px 10px', fontSize: 12, color: 'var(--text2)',
                          wordBreak: 'break-all' }}>
                          {e.ref || e.fichier || e.motif || e.detail || ''}
                          {e.trouve === false && (
                            <span style={{ color: 'var(--text3)' }}> — introuvable</span>
                          )}
                        </td>
                        <td style={{ padding: '6px 10px', fontSize: 11, color: 'var(--text3)',
                          whiteSpace: 'nowrap' }}>{e.ip || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
